import { h as hthvItem } from './_http-header-value.base.js';

/**
 * @internal
 */
function addParam({ p, pl, }, param) {
    const key = param.n || param.v;
    const prev = p[key];
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!prev || !prev.n && param.n) {
        p[key] = param;
    }
    pl.push(param);
}

/**
 * @internal
 */
function angleBracketsParser({ delimiterOf }) {
    return (input, out) => {
        if (!(delimiterOf(input.s[input.i]) & 262144 /* AngleBracketStart */)) {
            return false;
        }
        let result = '';
        ++input.i;
        while (input.i < input.s.length) {
            const c = input.s[input.i++];
            if (delimiterOf(c) & 524288 /* AngleBracketEnd */) {
                break;
            }
            result += c;
        }
        out(result);
        return true;
    };
}

/**
 * @internal
 */
const parseNone = () => false;

const datePattern = /^(Mon|Tue|Wed|Thu|Fri|Sat|Sun), \d\d (Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) \d\d\d\d \d\d:\d\d:\d\d GMT/;
/**
 * @internal
 */
function dateTimeParser({ dateTime }) {
    return dateTime ? parseDateTime : parseNone;
}
function parseDateTime(input, out) {
    input.s = input.s.substring(input.i);
    input.i = 0;
    if (datePattern.test(input.s)) {
        out(input.s.substring(input.i, input.i += 29));
        return true;
    }
    return false;
}

/**
 * @internal
 */
function nextInItem({ delimiterOf }) {
    return input => {
        const c = input.s[input.i];
        input.d = delimiterOf(c);
        return c;
    };
}

/**
 * @internal
 */
function quotedStringParser({ delimiterOf }) {
    return (input, out) => {
        let unquoted = '';
        ++input.i;
        for (; input.i < input.s.length; ++input.i) {
            const c = input.s[input.i];
            if (c === '\\') {
                const next = input.s[++input.i];
                if (next) {
                    unquoted += next;
                }
                else {
                    unquoted += c;
                }
            }
            else if (delimiterOf(c) & 65536 /* Quote */) {
                ++input.i;
                out(unquoted);
                return;
            }
            else {
                unquoted += c;
            }
        }
        out(unquoted);
    };
}

/**
 * @internal
 */
function itemParser(config, { named = true, tagged = true, extra = true, next = nextInItem(config), } = {}) {
    const parseAngleBrackets = angleBracketsParser(config);
    const parseDateTime = dateTimeParser(config);
    const parseQuotedString = quotedStringParser(config);
    const parseExtra = extra ? itemParser(config, { next, tagged: false, named: false, extra: false }) : parseNone;
    return (input, out) => {
        let name = '';
        let type = 'raw';
        let tag;
        let value;
        while (input.i < input.s.length) {
            const c = next(input);
            if (input.d) {
                if (input.d & (4 /* Item */ | 8 /* Parameter */)) {
                    break;
                }
                if (value == null) {
                    if (input.d & 16 /* Assignment */) {
                        value = name ? '' : c;
                        ++input.i;
                        continue;
                    }
                    if (input.d & 65536 /* Quote */) {
                        if (tagged || !name) {
                            parseQuotedString(input, v => {
                                if (name) {
                                    type = 'tagged-string';
                                    tag = name;
                                }
                                else {
                                    type = 'quoted-string';
                                }
                                name = '';
                                value = v;
                            });
                        }
                        break;
                    }
                    if (!name && parseAngleBrackets(input, v => {
                        type = 'angle-bracketed-string';
                        value = v;
                    })) {
                        break;
                    }
                    value = name;
                    name = '';
                }
                else if (input.d & 65536 /* Quote */) {
                    if (tagged || !value) {
                        parseQuotedString(input, v => {
                            if (value) {
                                type = 'tagged-string';
                                tag = value;
                            }
                            else {
                                type = 'quoted-string';
                            }
                            value = v;
                        });
                    }
                    break;
                }
                else if (!value && parseAngleBrackets(input, v => {
                    type = 'angle-bracketed-string';
                    value = v;
                })) {
                    break;
                }
            }
            if (value == null) {
                if (!name && parseDateTime(input, v => value = v)) {
                    type = 'date-time';
                    break;
                }
                if (named) {
                    name += c;
                }
                else {
                    value = c;
                }
            }
            else if (!value && parseDateTime(input, v => value = v)) {
                type = 'date-time';
                break;
            }
            else {
                value += c;
            }
            ++input.i;
        }
        let item;
        if (value == null) {
            if (!name) {
                return false;
            }
            item = hthvItem({ $: type, v: name });
        }
        else {
            item = hthvItem({ $: type, n: name || undefined, t: tag, v: value });
        }
        // noinspection StatementWithEmptyBodyJS
        while (parseExtra(input, extraItem => item.x.push(extraItem)))
            ; // eslint-disable-line curly
        out(item);
        return true;
    };
}

/**
 * @internal
 */
function nextInComment({ delimiterOf }) {
    return input => {
        let c = input.s[input.i];
        if (c !== '\\') {
            input.d = delimiterOf(c);
            return c;
        }
        ++input.i;
        if (input.i < input.s.length) {
            c = input.s[input.i];
            input.d = delimiterOf(c) ? 1 /* NonToken */ : 0 /* None */;
        }
        else {
            input.d = 1 /* NonToken */;
        }
        return c;
    };
}

/**
 * @internal
 */
function spacesParser({ delimiterOf }) {
    return input => {
        const start = input.i;
        do {
            const c = input.s[input.i];
            if (!(delimiterOf(c) & 2 /* Space */)) {
                break;
            }
            input.i++;
        } while (input.i < input.s.length);
        return input.i !== start;
    };
}

/**
 * @internal
 */
function paramParser(config, opts = {}) {
    const { delimiterOf } = config;
    const skipSpaces = spacesParser(config);
    const parseItem = itemParser(config, { ...opts, tagged: false });
    return (input, out) => {
        if (!(delimiterOf(input.s[input.i]) & 8 /* Parameter */)) {
            return false;
        }
        ++input.i;
        skipSpaces(input);
        return parseItem(input, out);
    };
}

/**
 * @internal
 */
function commentParser(config) {
    const { delimiterOf } = config;
    const skipSpaces = spacesParser(config);
    const commentParserConfig = {
        next: nextInComment(config),
    };
    const parseItem = itemParser(config, commentParserConfig);
    const parseParam = paramParser(config, commentParserConfig);
    return (input, out) => {
        if (!(delimiterOf(input.s[input.i]) & 131072 /* Comment */)) {
            return false;
        }
        ++input.i;
        let result;
        // noinspection StatementWithEmptyBodyJS
        while (skipSpaces(input)
            || parseParam(input, param => {
                if (!result) {
                    result = hthvItem({ $: 'raw', v: '' });
                }
                addParam(result, param);
            })
            || parseItem(input, item => result = item))
            ; // eslint-disable-line curly
        ++input.i; // closing parent
        out(result || hthvItem({ $: 'raw', v: '' }));
        return true;
    };
}

/**
 * @internal
 */
function itemDelimitParser(config) {
    const { delimiterOf } = config;
    const skipSpaces = spacesParser(config);
    return input => {
        if (delimiterOf(input.s[input.i]) & 4 /* Item */) {
            input.i++;
            skipSpaces(input);
            return true;
        }
        return false;
    };
}

/**
 * @internal
 */
const defaultDelimit = {
    ' ': 1 /* NonToken */ | 2 /* Space */ | 4 /* Item */,
    '\t': 1 /* NonToken */ | 2 /* Space */ | 4 /* Item */,
    ',': 1 /* NonToken */ | 4 /* Item */,
    ';': 1 /* NonToken */ | 8 /* Parameter */,
    '"': 1 /* NonToken */ | 32 /* Escaped */ | 65536 /* Quote */,
    '\\': 1 /* NonToken */ | 32 /* Escaped */,
    '<': 1 /* NonToken */ | 262144 /* AngleBracketStart */,
    '>': 1 /* NonToken */ | 524288 /* AngleBracketEnd */,
    '=': 1 /* NonToken */ | 16 /* Assignment */,
    '(': 1 /* NonToken */,
    ')': 1 /* NonToken */,
    '/': 1 /* NonToken */,
    ':': 1 /* NonToken */,
    '?': 1 /* NonToken */,
    '@': 1 /* NonToken */,
    '[': 1 /* NonToken */,
    ']': 1 /* NonToken */,
    '{': 1 /* NonToken */,
    '}': 1 /* NonToken */,
};
/**
 * @internal
 */
function buildParserConfig({ delimit, dateTime, } = {}) {
    const delimitConfig = delimit ? { ...defaultDelimit, ...delimit } : defaultDelimit;
    return {
        dateTime,
        delimiterOf(c) {
            return delimitConfig[c]
                || (c >= '\u0000' && c <= ' ' || c === '\u007f' ? 1 /* NonToken */ : 0 /* None */);
        },
    };
}
/**
 * @internal
 */
const defaultParserConfig = ( /*#__PURE__*/buildParserConfig());
/**
 * @internal
 */
const commentParserConfig = ( /*#__PURE__*/buildParserConfig({
    delimit: {
        ':': 1 /* NonToken */ | 16 /* Assignment */,
        '(': 1 /* NonToken */ | 32 /* Escaped */ | 131072 /* Comment */,
        ')': 1 /* NonToken */ | 32 /* Escaped */ | 4 /* Item */,
        ' ': 1 /* NonToken */ | 2 /* Space */,
        '\t': 1 /* NonToken */ | 2 /* Space */,
        '=': 1 /* NonToken */,
        ',': 1 /* NonToken */,
    },
}));

/**
 * Creates and configures new HTTP header value parser.
 *
 * @param config - New parser configuration.
 *
 * @returns New HTTP header value parser function.
 */
function newHthvParser(config) {
    const parserConfig = config ? buildParserConfig(config) : defaultParserConfig;
    const parseItemDelimit = itemDelimitParser(parserConfig);
    const parseParam = paramParser(parserConfig);
    const parseItem = itemParser(parserConfig);
    const parseComment = (config === null || config === void 0 ? void 0 : config.comments) ? commentParser(commentParserConfig) : parseNone;
    return headerValue => {
        const result = [];
        const input = { i: 0, s: headerValue };
        // noinspection StatementWithEmptyBodyJS
        while (parseTopLevelItem())
            ; // eslint-disable-line curly
        return result;
        function parseTopLevelItem() {
            return input.i < input.s.length && (parseItemDelimit(input)
                || parseParam(input, param => {
                    if (!result.length) {
                        result.push(hthvItem({ $: 'raw', v: '' }));
                    }
                    addParam(result[result.length - 1], param);
                })
                || parseComment(input, item => result.push(item))
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                || parseItem(input, item => result.push(item)));
        }
    };
}

/**
 * Parses HTTP header value that may contain comments.
 *
 * This is particularly useful when parsing headers such as:
 * - `User-Agent`
 * - `Server`
 */
const hthvParseCommented = ( /*#__PURE__*/newHthvParser({ comments: true }));

/**
 * Parses HTTP header values containing directives.
 *
 * Directives are parameterized items using spaces instead of semicolons to delimit parameters. While semicolons and
 * colons are used to delimit items.
 *
 * Date/time values within directives are usually enclosed into quotes. So, no need to parse them in a special way.
 *
 * This is particularly useful when parsing headers such as:
 * - `Content-Security-Policy`
 * - `Content-Security-Policy-Report-Only`
 * - `Feature-Policy`
 * - `Via`
 * - `Warning`
 *
 * @param value - HTTP header value to parse.
 *
 * @returns An array of comma-separated value items with space-separated parameters.
 */
const hthvParseDirectives = ( /*#__PURE__*/newHthvParser({
    delimit: {
        ' ': 1 /* NonToken */ | 2 /* Space */ | 8 /* Parameter */,
        '\t': 1 /* NonToken */ | 2 /* Space */ | 8 /* Parameter */,
        ';': 1 /* NonToken */ | 4 /* Item */,
    },
}));

/**
 * Parses semicolon-separated HTTP header value items.
 *
 * Treats ';' just like ','.
 *
 * This is particularly useful when parsing a `Cookies` header.
 *
 * The parsed items won't have any parameters.
 */
const hthvParseSemiSep = (
/*#__PURE__*/ newHthvParser({
    delimit: {
        ';': 1 /* NonToken */ | 4 /* Item */,
    },
}));

/**
 * Parses HTTP header value containing URI(s) without parameters.
 *
 * `,`, `;`, '(', ')', and `=` symbols can be part of URI. This function returns multiple items only if URIs are
 * space-separated.
 *
 * This is particularly useful when parsing headers such as:
 * - `Content-Location`
 * - `Location`
 * - `Referef`
 *
 * @param value - HTTP header value to parse.
 *
 * @returns An array of space-separated value items without parameters.
 */
const hthvParseURIs = (
/*#__PURE__*/ newHthvParser({
    delimit: {
        ',': 1 /* NonToken */,
        ';': 1 /* NonToken */,
        '=': 1 /* NonToken */,
    },
}));

/**
 * Escapes a `string` to be included into [quoted-string](https://tools.ietf.org/html/rfc7230#section-3.2.6) within HTTP
 * header value.
 *
 * Replaces `\` with `\\`, and `"` with `\"`.
 *
 * @param string - A string to escape.
 *
 * @returns Escaped `string`.
 */
function hthvEscapeQ(string) {
    return hthvEscape(string, defaultParserConfig);
}
/**
 * Escapes a `string` to be included into [comment](https://tools.ietf.org/html/rfc7230#section-3.2.6) withing HTTP
 * header value.
 *
 * Replaces `\` with `\\`, `"` with `\"`, `(` with `\(`, and `)` with `\)`.
 *
 * According to [RFC7230] the sender should not escape a double quote in this case. However, it may be necessary
 * to distinguish between raw double quote and nested [quoted-string].
 *
 * [RFC7230]: https://tools.ietf.org/html/rfc7230
 * [quoted-string]: https://tools.ietf.org/html/rfc7230#section-3.2.6
 *
 * @param string - A string to escape.
 *
 * @returns Escaped `string`.
 */
function hthvEscapeC(string) {
    return hthvEscape(string, commentParserConfig);
}
/**
 * @internal
 */
function hthvEscape(string, { delimiterOf }) {
    let escaped;
    for (let i = 0; i < string.length; ++i) {
        const c = string[i];
        if (delimiterOf(c) & 32 /* Escaped */) {
            if (!escaped) {
                escaped = string.substring(0, i);
            }
            escaped += '\\' + c;
        }
        else if (escaped) {
            escaped += c;
        }
    }
    return escaped || string;
}

/**
 * Flattens HTTP header value items by extracting their parameters.
 *
 * The result is an items collection containing original `items`, as well as their parameters.
 *
 * Recursively places `items` and their parameters to result map in their original order, and:
 * - prefers named items over unnamed ones,
 * - prefers original items over their parameters,
 * - prefers items added first.
 *
 * @typeParam TNameMode - Whether items has a {@link n name}.
 * @typeParam TTagMode - Whether items has a {@link t tag}.
 * @typeParam TParamsMode - Whether items has {@link p parameters}.
 * @param items Items collection.
 */
function hthvFlatten(items) {
    const map = {};
    const list = [];
    const depths = {};
    const put = (item, depth) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        list.push(item);
        const key = item.n || item.v;
        const prev = map[key];
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (prev) {
            if (item.n) {
                if (prev.n && depths[key] <= depth) {
                    return;
                }
            }
            else if (prev.n || depths[key] <= depth) {
                return;
            }
        }
        map[key] = item;
        depths[key] = depth;
    };
    const add = (item, depth) => {
        put(item, depth);
        ++depth;
        item.pl.forEach(p => add(p, depth));
    };
    items.forEach(item => add(item, 0));
    return { map, list };
}

/**
 * Parses HTTP header value.
 *
 * Splits the value onto {@link HthvItem items}.
 *
 * Parser is configured with default {@link HthvParserConfig config}.
 *
 * Does not recognize comments or date/time values.
 *
 * Treats illegal characters as ASCII letters.
 *
 * @param value - HTTP header value to parse.
 *
 * @returns An array of comma- or space- separated value items.
 */
const hthvParse = ( /*#__PURE__*/newHthvParser());
/**
 * Parses HTTP header value that may contain date/time.
 *
 * Splits the value onto {@link HthvItem items}.
 *
 * Parser is configured with default config except {@link HthvParserConfig.dateTime date/time parsing enabled}.
 *
 * Does not recognize comments.
 *
 * Handles date/time values in [IMF-fixdate] format only.
 *
 * Treats illegal characters as ASCII letters.
 *
 * [IMF-fixdate]: https://tools.ietf.org/html/rfc7231#section-7.1.1.1
 *
 * @param value - HTTP header value to parse.
 *
 * @returns An array of comma- or space- separated value items.
 */
const hthvParseDT = ( /*#__PURE__*/newHthvParser({ dateTime: true }));

/**
 * Conditionally encloses HTTP header value or its part into double quotes.
 *
 * Quotes will be added if delimiter or special character is present in input `string`, or the input `string` is empty.
 * {@link hthvEscapeQ Escapes} `"` and `\` symbols.
 *
 * @param string - A string to quote.
 *
 * @returns Either a string enclosed in double quotes, or original `string` if there is no need to quote it.
 */
function hthvQuote(string) {
    if (!string) {
        return '""';
    }
    const { delimiterOf } = defaultParserConfig;
    let escaped;
    let quote = false;
    for (let i = 0; i < string.length; ++i) {
        const c = string[i];
        const delimiter = delimiterOf(c);
        if (delimiter) {
            if (delimiter & 32 /* Escaped */) {
                if (!escaped) {
                    escaped = string.substring(0, i);
                }
                escaped += '\\' + c;
            }
            quote = true;
        }
        else if (escaped) {
            escaped += c;
        }
    }
    return quote ? `"${escaped || string}"` : string;
}

export { hthvEscapeC, hthvEscapeQ, hthvFlatten, hthvParse, hthvParseCommented, hthvParseDT, hthvParseDirectives, hthvParseSemiSep, hthvParseURIs, hthvQuote, newHthvParser };
//# sourceMappingURL=http-header-value.js.map
